import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowUp, Check, Copy, AlertCircle, Plus, FileSpreadsheet, Link2, ArrowDown, Pencil, ThumbsUp, ThumbsDown, X, Crosshair, StickyNote, AtSign, Gavel, FileText, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { ACCEPT, ACCEPT_ATTR } from "@/lib/intake";
import { filesFromDrop } from "@/lib/dropFiles";
import { cn } from "@/lib/utils";
import { LightMarkdown, UserAvatar } from "@/components/common";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ProposalCard, ContextNote } from "@/components/canvas/ProposalCard";
import { MentionMenu, PersonChip } from "@/components/team/TeamBits";
import { useMentions } from "@/components/team/NoteComposer";
import { ago } from "@/lib/sk";

const THINKING = {
  krav: ["Leser forespørselen…", "Finner arbeidspostene og kildesidene…", "Sjekker hva som mangler…"],
  kalkyle: ["Setter inn kostelementene…", "Regner om summene…"],
  tilbud: ["Går gjennom kundevisningen…", "Sjekker hva som mangler pris…"],
};

const copyText = (t, msg = "Kopiert") => { navigator.clipboard?.writeText(t || ""); toast.success(msg); };

function Thinking({ tab, labels }) {
  const list = labels || THINKING[tab] || THINKING.krav;
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % list.length), 2200);
    return () => clearInterval(t);
  }, [list.length]);
  return (
    <div className="msg-enter flex items-center gap-3 text-[14px] text-muted-foreground" data-testid="canvas-thinking">
      <span className="pulse-dot h-3 w-3 rounded-full bg-[hsl(var(--brand))]" />
      <span className="fade-in" key={i}>{list[i]}</span>
    </div>
  );
}

function useTypewriter(text, enabled) {
  const [n, setN] = useState(enabled ? 0 : text.length);
  useEffect(() => {
    if (!enabled) return undefined;
    let i = 0;
    const step = Math.max(2, Math.ceil(text.length / 90));
    const id = setInterval(() => {
      i = Math.min(text.length, i + step);
      setN(i);
      if (i >= text.length) clearInterval(id);
    }, 14);
    return () => clearInterval(id);
  }, [text, enabled]);
  return enabled ? text.slice(0, n) : text;
}

function AssistantText({ text, typewriter }) {
  const shown = useTypewriter(text, typewriter);
  return <LightMarkdown text={shown} className="text-[15px] leading-[1.65] text-foreground/90 [overflow-wrap:anywhere] [&_strong]:font-medium [&_strong]:text-foreground [&_ul]:mt-1.5 [&_ul]:space-y-0.5 [&_ol]:mt-1.5 [&_ol]:space-y-0.5 [&_li]:leading-[1.55] [&_p+p]:mt-2.5 [&_a]:break-all [&_em]:not-italic [&_em]:text-muted-foreground" />;
}

/** Small icon action under a message (ChatGPT-style hover row). */
function Action({ label, onClick, children, testId, active }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" onClick={onClick} aria-label={label} aria-pressed={active} data-testid={testId} className={cn("flex h-7 w-7 items-center justify-center rounded-[8px] text-foreground/45 outline-none transition-[color,background-color] hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40", active && "text-primary hover:text-primary")}>
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={4} className="rounded-lg px-2 py-1 text-[11.5px]">{label}</TooltipContent>
    </Tooltip>
  );
}

function LinkCard({ a }) {
  return (
    <div className="mt-3 flex items-center gap-2 rounded-[14px] border border-black/[0.07] bg-white px-3 py-2" data-testid="canvas-link-card">
      <Link2 className="h-3.5 w-3.5 shrink-0 text-primary" />
      <a href={a.link} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-foreground/80 hover:text-primary">{a.link.replace(/^https?:\/\//, "")}</a>
      <button type="button" onClick={() => copyText(a.link, "Lenke kopiert")} className="inline-flex h-7 items-center gap-1.5 rounded-full px-2 text-[12px] text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" data-testid="canvas-link-copy-button"><Copy className="h-3 w-3" /> Kopier</button>
    </div>
  );
}

/** A review («Strukturer på nytt») reports one calm line; the numbers sit behind «Vis gjennomgang» instead of filling the chat. */
function Review({ r, id }) {
  const [open, setOpen] = useState(false);
  if (!r) return null;
  return (
    <div className="mt-1.5 text-[12.5px] text-muted-foreground" data-testid={`canvas-review-${id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="rounded-sm underline-offset-2 hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring/40" data-testid={`canvas-review-toggle-${id}`}>{open ? "Skjul gjennomgang" : "Vis gjennomgang"}</button>
      {open ? <div className="mt-1 font-mono text-[12px] tabular-nums" data-testid={`canvas-review-detail-${id}`}>{r.detail}</div> : null}
    </div>
  );
}

/** What the assistant did in the canvas - quiet "worked" lines above the answer, like tool calls in ChatGPT. */
function Worked({ items }) {
  const list = items.filter((a) => a.label);
  if (!list.length) return null;
  return (
    <ul className="mb-2.5 space-y-1" data-testid="canvas-actions-done">
      {list.map((a, i) => (
        <li key={i} className="flex items-start gap-2 text-[12.5px] leading-[1.5] text-muted-foreground">
          {a.error ? <AlertCircle className="mt-[3px] h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-warn))]" /> : <Check className="mt-[3px] h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={2.6} />}
          <span className={cn(a.error && "text-foreground/80")}>{a.label}</span>
        </li>
      ))}
    </ul>
  );
}

function Files({ files, end }) {
  if (!files?.length) return null;
  return (
    <div className={cn("flex flex-wrap gap-1.5", end && "justify-end")}>
      {files.map((f, i) => <span key={i} className="inline-flex h-7 items-center gap-1.5 rounded-xl border border-black/[0.08] bg-white px-2.5 text-[11.5px]"><FileSpreadsheet className="h-3.5 w-3.5 text-primary" />{f}</span>)}
    </div>
  );
}

/** Note text with every @-mention set in navy – the names are plain text in storage, so the document stays readable anywhere. */
function NoteText({ content, mentions }) {
  const names = (mentions || []).map((m) => m.name).filter(Boolean).sort((a, b) => b.length - a.length);
  if (!names.length) return <>{content}</>;
  const re = new RegExp(`@(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  const parts = content.split(re);
  return <>{parts.map((p, i) => (i % 2 === 1 ? <span key={i} className="rounded-[4px] bg-[hsl(var(--brand)/0.08)] px-0.5 font-medium text-[hsl(var(--brand-strong))]">@{p}</span> : p))}</>;
}

/** Internal team dialogue: who wrote it, to whom, about which post/document and version – stored on the case, never a question to the KI, never an applied change. */
function NoteMessage({ m, animate, onGoto }) {
  const a = m.author || {};
  const an = m.anchor || {};
  const v = m.version || {};
  const decision = m.kind === "beslutning";
  const version = v.quote_revision ? `rev. ${v.quote_revision}` : v.settings_version ? `satser v${v.settings_version}` : null;
  return (
    <div className={cn("flex items-start gap-2.5", animate && "msg-enter")} data-testid="canvas-message-note" data-message-id={m.id} data-kind={m.kind || "notat"}>
      <UserAvatar name={a.name} initials={a.initials} color={a.color} src={a.avatar_url} size="md" className="mt-0.5 h-7 w-7" />
      <div className={cn("min-w-0 flex-1 rounded-[16px] rounded-tl-[6px] border px-3.5 py-2.5", decision ? "border-[hsl(var(--brand)/0.4)] bg-[hsl(var(--brand)/0.06)]" : "border-[hsl(var(--brand)/0.16)] bg-[hsl(var(--brand)/0.035)]")}>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12px]">
          <span className="font-semibold text-foreground">{a.name || "Ukjent"}</span>
          {decision ? <span className="inline-flex items-center gap-1 rounded-[4px] bg-[hsl(var(--brand))] px-1.5 py-px text-[10.5px] font-semibold text-white" title="Dokumentert beslutning. Ingenting er anvendt – endringer i kalkyle/tilbud gjøres eksplisitt og logges."><Gavel className="h-2.5 w-2.5" /> Beslutning · ikke anvendt</span>
            : <span className="inline-flex items-center gap-1 text-muted-foreground"><StickyNote className="h-3 w-3" /> internt notat</span>}
          <time className="ml-auto text-muted-foreground/80" dateTime={m.created_at}>{ago(m.created_at)}</time>
        </div>
        {an.item_ref || an.doc_name || version ? (
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11.5px]" data-testid="canvas-note-anchor">
            {an.item_ref ? <button type="button" onClick={() => onGoto?.("krav", an.item_ref)} className="inline-flex h-5 items-center gap-1 rounded-full border border-black/[0.08] bg-white px-1.5 font-mono text-[11px] text-foreground/80 hover:border-black/[0.25]" title={an.item_title || undefined} data-testid="canvas-note-anchor-item">{an.item_ref}{an.item_title ? <span className="max-w-[160px] truncate font-sans text-muted-foreground">{an.item_title}</span> : null}</button> : null}
            {an.doc_name ? <span className="inline-flex h-5 max-w-[220px] items-center gap-1 truncate rounded-full border border-black/[0.08] bg-white px-1.5 text-muted-foreground" title={an.doc_name}><FileText className="h-3 w-3 shrink-0" /><span className="truncate">{an.doc_name}</span></span> : null}
            {version ? <span className="text-muted-foreground/80" data-testid="canvas-note-version">{version}</span> : null}
          </div>
        ) : null}
        <div className="mt-1 whitespace-pre-wrap text-[14px] leading-[1.6] text-foreground" data-testid="canvas-note-text"><NoteText content={m.content} mentions={m.mentions} /></div>
        {m.mentions?.length ? <div className="mt-2 flex flex-wrap gap-1.5" data-testid="canvas-note-mentions">{m.mentions.map((p) => <span key={p.id} className="inline-flex h-6 items-center rounded-full border border-black/[0.08] bg-white pl-1 pr-2"><PersonChip person={p} size="xs" /></span>)}</div> : null}
      </div>
    </div>
  );
}

/** The quiet line after a proposal was carried out – result, consequence and ONE reversible action. Never a second bubble repeating the card. */
function AppliedLine({ m, proposal, undo, onUndo, busy, animate }) {
  const undone = proposal?.status === "angret";
  if (undone) return null; // the card above already says «Angret» – no second line repeating it
  const canUndo = undo && undo.proposal_id === m.applied_proposal && onUndo;
  return (
    <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted-foreground", animate && "msg-enter")} data-testid="canvas-message-applied" data-state="utfort">
      <Check className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--v-ok))]" strokeWidth={2.6} />
      <span className="min-w-0">{m.content}</span>
      {canUndo ? <button type="button" onClick={onUndo} disabled={busy} className="inline-flex h-7 items-center gap-1 rounded-full border border-black/[0.1] bg-white px-2.5 text-[12.5px] font-medium text-foreground/80 transition-colors hover:border-black/[0.25] hover:text-foreground disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="canvas-applied-undo"><Undo2 className="h-3 w-3" /> Angre</button> : null}
    </div>
  );
}

function Message({ m, isLast, onQuick, animate, customerLabel, typewriter, tools, onEdit, onFeedback, onDecide, busy, onGoto, proposals, undo, onUndo }) {
  if (m.role === "note") return <NoteMessage m={m} animate={animate} onGoto={onGoto} />;
  if (m.role === "assistant" && m.applied_proposal) return <AppliedLine m={m} proposal={proposals?.[m.applied_proposal]} undo={undo} onUndo={onUndo} busy={busy} animate={animate} />;
  if (m.role === "user" && m.author === "customer" && customerLabel) {
    return (
      <div className={cn("flex flex-col items-start gap-1", animate && "msg-enter")} data-testid="canvas-message-customer">
        <span className="pl-1 text-[11px] font-medium text-primary">{customerLabel}</span>
        {m.content ? <div className="max-w-[88%] whitespace-pre-wrap rounded-[18px] rounded-tl-[6px] bg-[hsl(var(--brand-soft))] px-4 py-2.5 text-[14px] leading-[1.6] text-foreground">{m.content}</div> : null}
        <Files files={m.attachments} />
      </div>
    );
  }
  if (m.role === "user") {
    const text = m.content?.length > 900 ? `${m.content.slice(0, 900)}…` : m.content;
    return (
      <div className={cn("group flex flex-col items-end gap-1", animate && "msg-enter")} data-testid="canvas-message-user">
        {m.content ? <div className="max-w-[85%] whitespace-pre-wrap rounded-[18px] bg-[hsl(210_20%_94.5%)] px-4 py-2.5 text-[15px] leading-[1.6] text-foreground">{text}</div> : null}
        <Files files={m.attachments} end />
        {tools && m.content ? (
          <div className="flex items-center gap-0.5 opacity-0 transition-opacity duration-150 focus-within:opacity-100 group-hover:opacity-100" data-testid={`canvas-user-actions-${m.id}`}>
            <Action label="Kopier" onClick={() => copyText(m.content)} testId={`canvas-msg-copy-${m.id}`}><Copy className="h-3.5 w-3.5" /></Action>
            <Action label="Rediger og send på nytt" onClick={() => onEdit(m.content)} testId={`canvas-msg-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Action>
          </div>
        ) : null}
      </div>
    );
  }
  const links = (m.actions_done || []).filter((a) => a.link);
  return (
    <div className={cn("group min-w-0", animate && "msg-enter")} data-testid="canvas-message-assistant">
      {m.actions_done?.length ? <Worked items={m.actions_done} /> : null}
      <AssistantText text={m.content} typewriter={Boolean(typewriter && animate)} />
      <Review r={m.ui?.review} id={m.id} />
      {m.proposal ? <ProposalCard p={m.proposal} busy={busy} onDecide={onDecide} /> : null}
      <ContextNote n={m.context_note} />
      {links.map((a, i) => <LinkCard key={i} a={a} />)}
      {tools ? (
        <div className={cn("-ml-1.5 mt-2 flex items-center gap-0.5 transition-opacity duration-150", isLast ? "opacity-100" : "opacity-0 focus-within:opacity-100 group-hover:opacity-100")} data-testid={`canvas-assistant-actions-${m.id}`}>
          <Action label="Kopier svaret" onClick={() => copyText(m.content, "Svaret er kopiert")} testId={`canvas-msg-copy-${m.id}`}><Copy className="h-3.5 w-3.5" /></Action>
          <Action label="Godt svar" active={m.feedback === "up"} onClick={() => onFeedback(m, m.feedback === "up" ? null : "up")} testId={`canvas-msg-up-${m.id}`}><ThumbsUp className="h-3.5 w-3.5" /></Action>
          <Action label="Dårlig svar" active={m.feedback === "down"} onClick={() => onFeedback(m, m.feedback === "down" ? null : "down")} testId={`canvas-msg-down-${m.id}`}><ThumbsDown className="h-3.5 w-3.5" /></Action>
        </div>
      ) : null}
      {isLast && m.quick_replies?.length && (!m.proposal || m.proposal.status === "pending") ? (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="canvas-quick-replies">
          {m.quick_replies.slice(0, 2).map((q, i) => (
            <button key={i} type="button" onClick={() => onQuick(q)} className="h-8 rounded-full border border-black/[0.08] bg-white px-3 text-[12.5px] font-medium text-foreground/75 transition-[border-color,background-color,color,transform] duration-150 hover:border-black/[0.18] hover:text-foreground active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="canvas-quick-reply">{q}</button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The steering column. Chat drives the work; the document on the right shows the result. */
export function ProjectChat({ messages, tab, busy, onSend, onNote, onGoto, onUpload, onFeedback, onDecide, undo = null, onUndo, className, placeholder, footnote, thinkingLabels, customerLabel = "Kunde", dropTitle, dropText, typewriter = false, header = null, uploads = null, context = null, onClearContext }) {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("ki"); // ki | notat – a note is stored on the case and never sent to the KI
  const [noteKind, setNoteKind] = useState("notat"); // notat | beslutning – a decision is documented, never applied
  const [atBottom, setAtBottom] = useState(true);
  const [drag, setDrag] = useState(0);
  const listRef = useRef(null);
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const mounted = useRef(false);
  const tools = Boolean(onFeedback);
  const canNote = Boolean(onNote);
  const noteMode = canNote && mode === "notat";
  const mn = useMentions(text, setText, taRef);
  const proposals = useMemo(() => Object.fromEntries(messages.filter((m) => m.proposal?.id).map((m) => [m.proposal.id, m.proposal])), [messages]);
  const trackMention = (value, caret) => { if (canNote) mn.track(value, caret); };
  const pickMention = (e) => { mn.pick(e); setMode("notat"); };

  const takeFiles = (list) => {
    const files = Array.from(list || []);
    if (!files.some((f) => ACCEPT.test(f.name))) return toast("Støttede filer: ZIP, PDF, Excel, Word, .msg-e-post, bilder og tegninger");
    if (busy) return toast("Vent til KI er ferdig, så laster jeg opp filene.");
    onUpload(files);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(0);
    filesFromDrop(e.dataTransfer).then(takeFiles);
  };
  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) {
      e.preventDefault();
      takeFiles(files);
    }
  };

  const scrollToEnd = (smooth = true) => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };

  useEffect(() => {
    scrollToEnd(mounted.current);
    mounted.current = true;
  }, [messages, busy]);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "0px";
    ta.style.height = `${Math.min(Math.max(ta.scrollHeight, 24), 220)}px`;
  }, [text]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 48);
  };

  const submit = () => {
    const t = text.trim();
    if (!t || busy) return;
    setText("");
    mn.clear();
    if (noteMode) {
      onNote(t, { item_id: context?.id || null, kind: noteKind });
      setNoteKind("notat");
      return;
    }
    onSend(t);
  };
  const edit = (content) => {
    setText(content);
    requestAnimationFrame(() => { const ta = taRef.current; if (ta) { ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length); } });
  };
  const lastIdx = messages.length - 1;

  return (
    <div
      className={cn("relative flex min-h-0 flex-col", className)}
      data-testid="canvas-chat"
      onDragEnter={(e) => { if (e.dataTransfer?.types?.includes("Files")) { e.preventDefault(); setDrag((v) => v + 1); } }}
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes("Files")) e.preventDefault(); }}
      onDragLeave={() => setDrag((v) => Math.max(0, v - 1))}
      onDrop={onDrop}
    >
      {drag > 0 ? (
        <div className="pointer-events-none absolute inset-2 z-20 flex items-center justify-center rounded-[22px] border-2 border-dashed border-primary/60 bg-white/85 backdrop-blur-sm" data-testid="canvas-dropzone">
          <div className="flex flex-col items-center gap-2 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-primary"><FileSpreadsheet className="h-5 w-5" /></span>
            <div className="text-[14px] font-semibold text-[hsl(var(--brand-strong))]">{dropTitle || "Slipp filene her"}</div>
            <div className="text-[12.5px] text-muted-foreground">{dropText || "Forespørsel, svarark, leverandørtilbud eller e-post – alt legges på saken med kilde"}</div>
          </div>
        </div>
      ) : null}
      {header}
      <div ref={listRef} onScroll={onScroll} className="scroll-fade-y no-scrollbar min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-6 sm:px-7">
        <div className="mx-auto flex max-w-[680px] flex-col gap-6">
          {messages.map((m, i) => <Message key={m.id} m={m} isLast={i === lastIdx && !busy} onQuick={onSend} animate={i === lastIdx && (mounted.current || Date.now() - new Date(m.created_at).getTime() < 20000)} customerLabel={customerLabel} typewriter={typewriter} tools={tools} onEdit={edit} onFeedback={onFeedback} onDecide={onDecide} busy={busy} onGoto={onGoto} proposals={proposals} undo={undo} onUndo={onUndo} />)}
          {busy ? <Thinking tab={tab} labels={thinkingLabels} /> : null}
        </div>
      </div>
      {!atBottom ? (
        <button type="button" onClick={() => scrollToEnd(true)} className="absolute bottom-[134px] left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border border-black/[0.08] bg-white text-foreground/70 shadow-md-soft hover:text-foreground" aria-label="Til bunnen" data-testid="canvas-scroll-bottom-button">
          <ArrowDown className="h-4 w-4" />
        </button>
      ) : null}
      <div className="shrink-0 px-3 pb-3 sm:px-4">
        <div className="mx-auto max-w-[680px]">
          {uploads}
          {context ? (
            <div className="mb-1.5 flex items-center gap-1.5 px-2" data-testid="canvas-context-chip">
              <span className="inline-flex h-7 max-w-full items-center gap-1.5 rounded-full bg-[hsl(var(--brand)/0.08)] pl-2.5 pr-1 text-[12.5px] text-[hsl(var(--brand-strong))]">
                <Crosshair className="h-3 w-3 shrink-0" />
                <span className="truncate">Post <span className="font-mono">{context.ref}</span>{context.title ? ` · ${context.title}` : ""}</span>
                {onClearContext ? <button type="button" onClick={onClearContext} className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full hover:bg-[hsl(var(--brand)/0.12)] focus-visible:ring-2 focus-visible:ring-ring/40" aria-label="Fjern valgt post fra samtalen" data-testid="canvas-context-clear"><X className="h-3 w-3" /></button> : null}
              </span>
              <span className="truncate text-[11.5px] text-muted-foreground/70">«denne posten» gjelder {context.ref}</span>
            </div>
          ) : null}
          <div className={cn("relative rounded-[22px] border bg-white shadow-[0_1px_2px_hsl(212_40%_16%/0.04),0_12px_32px_-24px_hsl(212_40%_16%/0.4)] transition-[border-color,box-shadow] duration-150 focus-within:shadow-[0_1px_2px_hsl(212_40%_16%/0.05),0_18px_44px_-24px_hsl(212_40%_16%/0.5)]", noteMode ? "border-[hsl(var(--brand)/0.35)] focus-within:border-[hsl(var(--brand)/0.55)]" : "border-black/[0.08] focus-within:border-black/[0.16]")} data-testid="canvas-composer" data-mode={noteMode ? "notat" : "ki"}>
            {mn.mention && mn.candidates.length ? <MentionMenu query={mn.mention.query} index={mn.idx} onPick={pickMention} /> : null}
            <textarea
              ref={taRef}
              rows={1}
              value={text}
              onChange={(e) => { setText(e.target.value); trackMention(e.target.value, e.target.selectionStart ?? e.target.value.length); }}
              onClick={(e) => trackMention(e.target.value, e.target.selectionStart ?? 0)}
              onPaste={onPaste}
              onKeyDown={(e) => {
                if (mn.onKeyDown(e)) { if (e.key === "Enter" || e.key === "Tab") setMode("notat"); return; }
                if (e.key === "Escape" && !text.trim()) {
                  if (noteMode) { setMode("ki"); setNoteKind("notat"); }
                  else if (context && onClearContext) onClearContext();
                  return;
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder={noteMode ? (noteKind === "beslutning" ? "Hva ble besluttet, av hvem og hvorfor – dokumenteres, anvendes ikke automatisk…" : `Internt notat${context?.ref ? ` om ${context.ref}` : " til teamet"} – skriv @ for å nevne en person eller avdeling…`) : placeholder || "Spør om hva som helst – eller slipp en fil…"}
              className="caret-brand block max-h-[220px] w-full resize-none bg-transparent px-4 pb-1 pt-3.5 text-[15px] leading-[1.55] outline-none placeholder:text-muted-foreground/55"
              data-testid="canvas-composer-input"
              aria-label={noteMode ? "Notat til teamet" : "Melding"}
            />
            <div className="flex items-center justify-between gap-2 px-2 pb-2 pt-1">
              <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                <input ref={fileRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden" onChange={(e) => { if (e.target.files?.length) takeFiles(e.target.files); e.target.value = ""; }} data-testid="canvas-file-input" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-foreground/65 transition-colors hover:bg-black/[0.05] hover:text-foreground disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-ring/40" aria-label="Legg ved filer" data-testid="canvas-attach-button">
                      <Plus className="h-5 w-5" strokeWidth={2} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="rounded-lg px-2.5 py-1 text-[12px]">Legg ved ZIP, PDF, Excel, Word eller .msg-e-post</TooltipContent>
                </Tooltip>
                {canNote ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" onClick={() => { setMode(noteMode ? "ki" : "notat"); mn.clear(); }} aria-pressed={noteMode} className={cn("inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium transition-[color,background-color]", noteMode ? "bg-[hsl(var(--brand))] text-white hover:bg-[hsl(var(--brand-strong))]" : "text-foreground/65 hover:bg-black/[0.05] hover:text-foreground")} data-testid="canvas-mode-notat">
                        <StickyNote className="h-4 w-4" strokeWidth={2} /> Notat
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="rounded-lg px-2.5 py-1 text-[12px]">{noteMode ? "Tilbake til KI – skriv til assistenten" : "Internt notat til teamet – går ikke til KI"}</TooltipContent>
                  </Tooltip>
                ) : null}
                {noteMode ? (
                  <>
                    <button type="button" onClick={mn.insertAt} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-foreground/65 hover:bg-black/[0.05] hover:text-foreground" aria-label="Nevn en person eller avdeling" title="Nevn en person eller avdeling" data-testid="canvas-mention-button"><AtSign className="h-4 w-4" /></button>
                    <button type="button" onClick={() => setNoteKind((k) => (k === "beslutning" ? "notat" : "beslutning"))} aria-pressed={noteKind === "beslutning"} aria-label="Marker som beslutning" className={cn("inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[12.5px] transition-colors", noteKind === "beslutning" ? "bg-[hsl(var(--brand)/0.1)] text-[hsl(var(--brand-strong))]" : "text-foreground/65 hover:bg-black/[0.05] hover:text-foreground")} title="Marker som dokumentert beslutning – anvendes ikke automatisk" data-testid="canvas-note-kind-toggle"><Gavel className="h-4 w-4" />{noteKind === "beslutning" ? "Beslutning" : null}</button>
                  </>
                ) : null}
              </div>
              <button type="button" onClick={submit} disabled={!text.trim() || busy} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand))] text-white transition-[transform,opacity,background-color] duration-150 hover:bg-[hsl(var(--brand-strong))] enabled:hover:scale-[1.04] disabled:bg-black/[0.08] disabled:text-foreground/40 focus-visible:ring-2 focus-visible:ring-ring/50" data-testid="canvas-composer-send-button" aria-label={noteMode ? "Lagre notat" : "Send"}>
                {noteMode ? <StickyNote className="h-4 w-4" strokeWidth={2.2} /> : <ArrowUp className="h-[18px] w-[18px]" strokeWidth={2.4} />}
              </button>
            </div>
          </div>
          <p className="mt-2 text-center text-[11px] text-muted-foreground/55" data-testid="canvas-composer-footnote">{noteMode ? (noteKind === "beslutning" ? "Beslutningen dokumenteres på saken og anvendes ikke automatisk." : "Internt notat – lagres på saken, går ikke til KI, endrer ingenting.") : footnote || "KI foreslår – du godkjenner. Summer regnes av systemet."}</p>
        </div>
      </div>
    </div>
  );
}
